import {
    CheckboxField,
    Box,
    GridItem,
    Grid,
    RadioField,
    RadioInput,
    Message
} from '@flos/react-ui';
import AccordionWizardTemplate from '../components/AccordionWizardTemplate';
import { externalLinks } from '../../../strings';
import { useRef, useState } from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { allCoverablesAtom } from '../../../recoil/atoms/allCoverablesAtom';
import { issuedCoverablesAndPricesAtom } from '../../../recoil/atoms/issuedCoverablesAndPricesAtom';
import {
    allCoverablesToCoverableDTOArray,
    getEarliestEffectiveCoverable
} from '../../../utils/coverablesUtil';
import { externalCasesAtom } from '../../../recoil/atoms/externalCasesAtom';
import { useIssueInsurance } from '../../../hooks/useIssueInsurance';
import { useAwaitBoundJob } from '../../../hooks/useAwaitBoundJob';
import { isAnyEdiCancellationCasePending } from '../../../utils/externalCasesUtil';
import { useHippoText } from '../../../hooks/useHippoText';
import { trackPurchase } from '../../../utils/trackingUtil';
import { jobInfoAtom } from '../../../recoil/atoms/jobInfoAtom';
import { accountHolderAtom } from '../../../recoil/atoms/accountHolderAtom';
import CallMeModal from '../../../shared_components/CallMeModal/CallMeModal';
import { businessFlowSelector } from '../../../recoil/selectors/getBusinessFlowSelector';
import { useUpdateHasInsuranceDebts } from '../../../hooks/useUpdateHasInsuranceDebts';
import type { YesNo_Top } from 'gw-edge-client/dist/types/dto/pc/typekey';
import type { HasInsuranceDebtsDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/common';
import useWizardNavigation from '../../../hooks/useWizardNavigation';
import _ from 'lodash';
import type {
    AllCoverablesDTO,
    CoverableDTO
} from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/quote';
import { fetchedPaymentInformationAtom } from '../../../recoil/atoms/fetchedPaymentInformationAtom';
import { PAYMENT_PLAN } from '../../../constants/paymentMethods';
import usePageNavigation from '../../../hooks/usePageNavigation';
import { useGetMpsActivationRule } from '../../../hooks/useGetMpsActivationRule';
import LoadingModal from '../../../shared_components/LoadingModal/LoadingModal';
import { jobAtom } from '../../../recoil/atoms/jobAtom';
import { FLAVOUR_NAME_MAP, LOB_CODE } from '../../../constants/coverable';
import {
    convertDateToLocalDateDTO,
    convertLocalDateDTOToDate,
    isDateInThePast
} from '../../../utils/dateUtil';
import type { ExternaleCaseDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/policyjob/edi/dto';
import type { AgriculturePropertyDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/pra';
import type { JobStatusDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto';
import { getEffectiveDateRuleInfoSelector } from '../../../recoil/selectors/getEffectiveDateRuleSelector';

const CompletePurchaseStep = ({ goNext }: { goNext: () => void }): React.JSX.Element => {
    const { issueInsurance } = useIssueInsurance();
    const { updateHasInsuranceDebts } = useUpdateHasInsuranceDebts();
    const { jobNumber } = useRecoilValue(jobInfoAtom);
    const [jobData, setJobData] = useRecoilState(jobAtom);
    const [showLoadingModal, setShowLoadingModal] = useState(false);
    const [isCorrectInfoChecked, setIsCorrectInfoChecked] = useState(false);
    const accountHolder = useRecoilValue(accountHolderAtom);
    const [isFactsAndConditionsChecked, setIsFactsAndConditionsChecked] = useState(false);
    const [allCoverables, setAllCoverables] = useRecoilState(allCoverablesAtom);
    const refreshCoverableAndPricesSelectors = useSetRecoilState(issuedCoverablesAndPricesAtom);
    const coverables = allCoverablesToCoverableDTOArray(allCoverables);
    const externalCases = useRecoilValue(externalCasesAtom);
    const motorChangeAfter14daysDTO = useRecoilValue(getEffectiveDateRuleInfoSelector);
    const isMotorOPCChangeAfter14days = motorChangeAfter14daysDTO.isRuleActive && jobData.isOpcJob;
    const [showModal, setShowModal] = useState(false);
    const [hasInsuranceDebtsRKI, setHasInsuranceDebtsRKI] = useState<YesNo_Top>();
    const getHippoText = useHippoText();
    const { awaitBoundJob } = useAwaitBoundJob(isAnyEdiCancellationCasePending(externalCases));
    const hasTriggeredIssuing = useRef(false);
    const [isLoading, setIsLoading] = useState(false);
    const [policyNumber, setPolicyNumber] = useState<string | undefined>();
    const businessFlow = useRecoilValue(businessFlowSelector);
    const { isCVR } = accountHolder;
    const { wizardGotoStep } = useWizardNavigation();
    const { paymentMethod } = useRecoilValue(fetchedPaymentInformationAtom);
    const { goToMPSActivationPage } = usePageNavigation();
    const shouldActivateMPS = useGetMpsActivationRule();
    const shouldHideEDIText = coverables.every(
        (obj) => obj.flavourName.toLowerCase() === FLAVOUR_NAME_MAP.top_cho_insurance
    );
    const subheaderInfoText =
        coverables.length > 1
            ? getHippoText('DSS-CHECKOUT-SCREEN-SUBHEADER-MULTIPLE')
            : getHippoText('DSS-CHECKOUT-SCREEN-SUBHEADER');

    const getSubheaderPaymentText = (): string => {
        if (jobData.isOpcJob) return getHippoText('DSS-SUMMARY-BUTTON-CONFIRM-CHANGES');
        if (paymentMethod === PAYMENT_PLAN.BS) {
            return coverables.length > 1
                ? getHippoText(
                      isCVR
                          ? 'DSS-CHECKOUT-SCREEN-SUBHEADER-CVR-BS-MULTIPLE'
                          : 'DSS-CHECKOUT-SCREEN-SUBHEADER-BS-MULTIPLE'
                  )
                : getHippoText(
                      isCVR
                          ? 'DSS-CHECKOUT-SCREEN-SUBHEADER-CVR-BS'
                          : 'DSS-CHECKOUT-SCREEN-SUBHEADER-BS'
                  );
        } else if (paymentMethod === PAYMENT_PLAN.MPS) {
            return coverables.length > 1
                ? getHippoText('DSS-CHECKOUT-SCREEN-SUBHEADER-MPS-MULTIPLE')
                : getHippoText('DSS-CHECKOUT-SCREEN-SUBHEADER-MPS');
        }

        return coverables.length > 1
            ? getHippoText('DSS-CHECKOUT-SCREEN-SUBHEADER-FIK-MULTIPLE')
            : getHippoText('DSS-CHECKOUT-SCREEN-SUBHEADER-FIK');
    };

    const showContentRKISection =
        businessFlow === 'ONSA' &&
        !jobData.isOpcJob &&
        (!_.isEmpty(allCoverables.cars) ||
            !_.isEmpty(allCoverables.privateContent) ||
            !_.isEmpty(allCoverables.youthContent));

    const updateCoverablesEffectiveDateWithExternalCases = (
        externalCases: ExternaleCaseDTO[],
        coverable: CoverableDTO
    ): CoverableDTO => {
        const matchingExternalCase = externalCases.find(
            (extCase) =>
                extCase.linkedEntityFixedId === coverable.fixedId &&
                extCase.lobCode?.substring(0, 3) === coverable.lobCode
        );
        if (matchingExternalCase) {
            return {
                ...coverable,
                effectiveDate:
                    matchingExternalCase.startDate ?? matchingExternalCase.cancellationDate
            };
        } else if (
            coverable.status === 'Changed' &&
            coverable.effectiveDate &&
            !isMotorOPCChangeAfter14days
        ) {
            return {
                ...coverable,
                effectiveDate: isDateInThePast(convertLocalDateDTOToDate(coverable.effectiveDate))
                    ? convertDateToLocalDateDTO(new Date())
                    : coverable.effectiveDate
            };
        } else {
            return coverable;
        }
    };

    const updatePRAEffectiveDateWithExternalCases = (
        externalCases: ExternaleCaseDTO[],
        praCoverable: AgriculturePropertyDTO
    ): AgriculturePropertyDTO => {
        const allPraLocations = praCoverable.propertyLocations || [];
        const updatedPRALocations = allPraLocations.map((praLoc) => {
            return updateCoverablesEffectiveDateWithExternalCases(externalCases, praLoc);
        });
        return {
            ...praCoverable,
            propertyLocations: updatedPRALocations,
            effectiveDate:
                getEarliestEffectiveCoverable(updatedPRALocations)?.effectiveDate ||
                praCoverable.effectiveDate
        };
    };

    const handleCompletePurchase = (): void => {
        const updatedAllCoverables = Object.keys(allCoverables).reduce<AllCoverablesDTO>(
            (prev, flavour) => {
                const flavourToUpdate = allCoverables[flavour as keyof AllCoverablesDTO];
                if (!flavourToUpdate) return { ...prev };
                const updatedValue = [...flavourToUpdate].map((cov) => {
                    if (cov.lobCode === LOB_CODE.PROPERTY) {
                        const updatedPraCoverable = updatePRAEffectiveDateWithExternalCases(
                            externalCases,
                            cov as AgriculturePropertyDTO
                        );
                        return updatedPraCoverable;
                    } else {
                        return updateCoverablesEffectiveDateWithExternalCases(externalCases, cov);
                    }
                });
                return { ...prev, [flavour as keyof AllCoverablesDTO]: updatedValue };
            },
            {}
        );
        setAllCoverables(updatedAllCoverables);
        setShowLoadingModal(true);
    };

    const onNextClick = async (): Promise<void> => {
        setIsLoading(true);
        if (showContentRKISection) {
            const concatenatedArray = [
                ...(allCoverables.cars ?? []),
                ...(allCoverables.privateContent ?? []),
                ...(allCoverables.youthContent ?? [])
            ];
            const updateHasInsuranceDebtsDTOs: HasInsuranceDebtsDTO[] = [];
            concatenatedArray?.forEach((item) => {
                updateHasInsuranceDebtsDTOs.push({
                    productToUpdate: item.flavourName,
                    coverableFixedId: item.fixedId,
                    hasInsuranceDebts: hasInsuranceDebtsRKI
                });
            });
            await updateHasInsuranceDebts(updateHasInsuranceDebtsDTOs);

            if (hasInsuranceDebtsRKI === 'Yes') {
                setShowModal(true);
                setIsLoading(false);
                return;
            }
        }
        handleCompletePurchase();
    };

    const issueJobInBackground = async (): Promise<void> => {
        if (hasTriggeredIssuing.current) return;
        hasTriggeredIssuing.current = true;
        const newPolicyNumber = await issueInsurance();
        let jobStatus: JobStatusDTO = { status: 'Bound', policyNumber: newPolicyNumber };
        if (_.isEmpty(newPolicyNumber)) {
            jobStatus = await awaitBoundJob();
        }
        setPolicyNumber(jobStatus.policyNumber);
        setJobData((jobDTO) => ({ ...jobDTO, jobStatus: jobStatus.status }));
    };

    const onFinished = (): void => {
        setShowLoadingModal(false);
        trackPurchase(coverables, jobNumber);
        if (shouldActivateMPS && policyNumber) {
            goToMPSActivationPage(policyNumber);
        } else {
            if (coverables.some((cov) => cov.willSpinOff)) {
                refreshCoverableAndPricesSelectors({});
            }
            wizardGotoStep('ReceiptPage');
        }
    };

    const subheaderCVRText = isCVR
        ? getHippoText('DSS-CHECKOUT-SCREEN-SUBHEADER-CVR')
        : getHippoText('DSS-CHECKOUT-SCREEN-SUBHEADER');

    const purchaseConditionsLink = isCVR
        ? externalLinks.purchaseConditionsCVRAgriculture
        : externalLinks.insuranceConditions;

    return (
        <AccordionWizardTemplate
            continueButtonText={getHippoText(
                !jobData.isOpcJob
                    ? 'DSS-CHECKOUT-SCREEN-COMPLETE-PURCHASE-BUTTON'
                    : 'DSS-COVERABLE-DETAILS-ACCEPT'
            )}
            goNext={() => {
                void onNextClick();
            }}
            disableNext={
                (!jobData.isOpcJob && !isCorrectInfoChecked) ||
                !isFactsAndConditionsChecked ||
                (showContentRKISection && !hasInsuranceDebtsRKI)
            }
            isNextBtnLoading={isLoading}
        >
            <GridItem span={12}>
                <Grid gap={6}>
                    {!shouldHideEDIText && !jobData.isOpcJob && (
                        <GridItem span={12}>
                            {isCVR ? subheaderCVRText : subheaderInfoText}
                        </GridItem>
                    )}
                    <GridItem span={12}>{getSubheaderPaymentText()}</GridItem>
                    {!jobData.isOpcJob && (
                        <GridItem span={12}>
                            <Message
                                title={getHippoText('DSS-INFO-TEXT-CHECKOUT-PAGE-CONSENT-SECTION')}
                            />
                        </GridItem>
                    )}
                    <GridItem span={12}>
                        <Box className="u-spacing-inset-bottom-none">
                            {!jobData.isOpcJob && (
                                <CheckboxField
                                    data-testid="correct-info-checkbox"
                                    onChange={(checkBoxEvent) => {
                                        setIsCorrectInfoChecked(checkBoxEvent.target.checked);
                                    }}
                                    label={getHippoText(
                                        isCVR
                                            ? 'DSS-CHECKOUT-SCREEN-CVR-CORRECT-INFO-CHECKBOX'
                                            : 'DSS-CHECKOUT-SCREEN-CORRECT-INFO-CHECKBOX'
                                    )}
                                />
                            )}
                            <CheckboxField
                                data-testid="facts-and-cond-checkbox"
                                onChange={(checkBoxEvent) => {
                                    setIsFactsAndConditionsChecked(checkBoxEvent.target.checked);
                                }}
                                label={
                                    <>
                                        {getHippoText(
                                            'DSS-CHECKOUT-SCREEN-FACTS-AND-CONDITIONS-CHECKBOX'
                                        ) + ' '}
                                        <a
                                            className={'u-color-primary-2-base'}
                                            href={externalLinks.insuranceFacts}
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            {coverables?.length === 1
                                                ? getHippoText(
                                                      'DSS-CHECKOUT-SCREEN-FACTS-AND-CONDITIONS-CHECKBOX-TEXT-FACT-ONE'
                                                  ) + ' '
                                                : getHippoText(
                                                      'DSS-CHECKOUT-SCREEN-FACTS-AND-CONDITIONS-CHECKBOX-TEXT-FACT-MULTIPLE'
                                                  ) + ' '}
                                        </a>
                                        {getHippoText(
                                            'DSS-CHECKOUT-SCREEN-FACTS-AND-CONDITIONS-CHECKBOX-TEXT'
                                        ) + ' '}
                                        <a
                                            className={'u-color-primary-2-base'}
                                            href={purchaseConditionsLink}
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            {getHippoText(
                                                'DSS-CHECKOUT-SCREEN-FACTS-AND-CONDITIONS-CHECKBOX-CONDITIONS'
                                            )}
                                        </a>
                                    </>
                                }
                            />
                        </Box>
                    </GridItem>
                    {showContentRKISection && (
                        <GridItem span={12}>
                            <Box>
                                <RadioField
                                    description={getHippoText(
                                        'DSS-CHECKOUT-SCREEN-REGISTERED-RKI-QUESTION'
                                    )}
                                    name="hasInsuranceDebtsRKI"
                                    value={hasInsuranceDebtsRKI?.toString() ?? ''}
                                    onChange={(e) => {
                                        setHasInsuranceDebtsRKI(e.target.value as YesNo_Top);
                                    }}
                                    className="h6"
                                >
                                    <RadioInput value="Yes" label="Ja" id="RKIYes" />
                                    <RadioInput value="No" label="Nej" id="RKINo" />
                                </RadioField>
                            </Box>
                        </GridItem>
                    )}
                </Grid>
            </GridItem>
            {showModal && (
                <CallMeModal
                    title={getHippoText('DSS-EFFECTIVE-DATE-CALL-ME-MODAL-MISS-HEADER')}
                    description={getHippoText('DSS-EFFECTIVE-DATE-CALL-ME-MODAL-MISS-SUBHEADER')}
                    activityCode="dss_call_request"
                    showModal={showModal}
                    closeModal={() => {
                        setShowModal(false);
                    }}
                />
            )}
            {showLoadingModal && (
                <LoadingModal
                    loadingTitle={getHippoText('DSS-LOADING-HEADER')}
                    loadingText={getHippoText('DSS-LOADING-TEXT')}
                    showModal={showLoadingModal}
                    closeModal={() => {
                        setShowLoadingModal(false);
                    }}
                    backgroundTask={issueJobInBackground}
                    onFinished={onFinished}
                />
            )}
        </AccordionWizardTemplate>
    );
};

export default CompletePurchaseStep;
